// @flow

import React, { type Node, useContext } from 'react';
import { forEach, get, isEmpty, isEqual, omit, pick, reduce } from 'lodash';

import { Screen } from '@zapier/theme';
import { UserAgentContext } from '@zapier/common-components';

import findMatchingQueries from './findMatchingQueries';
import guessUserAgentInfo from './guessUserAgentInfo';

type MediaQueryLists = {
  [string]: {
    [string]: (fn: any) => {},
  },
};

type SharedProps = {
  /** children function that media query results will be passed in to */
  children: ({ [key: string]: boolean }) => Node,
  /** Object where keys are query names and properties are the query e.g.
  ```
  { large: '(min-width: 600px)' }
  ```
   */
  queries?: Object,
};

type Props = {
  ...$Exact<SharedProps>,
  userAgent: string,
};

type State = {
  matches: Object,
};

/**
 * Injects media query results into children function so that children can
 * respond to changing media queries
 */
class InnerMedia extends React.Component<Props, State> {
  static defaultProps = {
    queries: {},
  };

  mediaQueryLists = ({}: MediaQueryLists);

  constructor(props) {
    super(props);
    const userAgentInfo = guessUserAgentInfo(props.userAgent);
    this.state = {
      // On SSR there's no way to know the screen size,
      // so this makes a best guess attempt based on the UA.
      matches: findMatchingQueries(userAgentInfo, Object.keys(props.queries)),
    };
  }

  componentDidMount() {
    this.configureMediaQueries(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!isEqual(omit(this.props, 'children'), omit(nextProps, 'children'))) {
      this.configureMediaQueries(nextProps);
    }
  }

  componentWillUnmount() {
    this.bindMediaQueryListeners(false);
    this.mediaQueryLists = {};
  }

  /**
   * Binds / unbinds event listeners for each media query
   */
  bindMediaQueryListeners = (shouldBind: boolean) => {
    const { mediaQueryLists } = this;
    const method = shouldBind ? 'addListener' : 'removeListener';
    forEach(mediaQueryLists, mediaQuery => {
      const methodFn = get(mediaQuery, method);
      if (methodFn) {
        methodFn.call(mediaQuery, this.setMatchesState);
      }
    });
  };

  buildMediaQueryLists = (queries: Object) => {
    this.mediaQueryLists = reduce(
      queries,
      (lists, query, key) => ({
        // Allow use of keys from screen variables
        [key]: window.matchMedia(Screen.queries[key] || query),
        ...lists,
      }),
      {}
    );
  };

  configureMediaQueries = ({ queries, ...rest }: Props) => {
    // remove all current bindings
    this.bindMediaQueryListeners(false);
    // create new list of media queries to bind
    this.buildMediaQueryLists({
      ...queries,
      ...pick(rest, Object.keys(Screen.queries)),
    });
    // bind new media queries
    this.bindMediaQueryListeners(true);
    // update current state for current render
    this.setMatchesState();
  };

  setMatchesState = () => {
    const { mediaQueryLists } = this;
    if (isEmpty(mediaQueryLists)) {
      return;
    }
    // set / unset flag for each media query that matches / doesn't match
    const matches = Object.keys(mediaQueryLists).reduce(
      (result, key) => ({
        [key]: mediaQueryLists[key].matches,
        ...result,
      }),
      {}
    );
    this.setState({ matches });
  };

  render() {
    const { children } = this.props;
    const { matches } = this.state;
    // send matches to children function
    return children({ ...matches });
  }
}

/**
 * @deprecated **Hey! Listen! ✨ This component is semi-deprecated. You might want to try `useMediaProps` instead.**
 */
const Media = (props: SharedProps) => {
  const userAgent = useContext(UserAgentContext);
  return <InnerMedia {...props} userAgent={userAgent} />;
};

export default Media;
